<template>
  <CRow>
    <CCol col="12" xl="12">
      <transition name="slide">
        <CCard v-if="isResetRequested === false">
          <CCardBody>
            <CAlert :color="alertType" v-if="message"> {{ message }}</CAlert>
            <CRow style="margin-bottom: 1rem;">
              <CCol>
                <div style="align-self: center; display: flex; align-items: center;">
                  <CIcon name="cilCalendarCheck" style="margin-right: 0.5rem" />
                  <h4>Bookings</h4>
                </div>
              </CCol>
            </CRow>
            <CRow style="margin-bottom: 1rem;">
              <CCol class="action-buttons">
                <div class="flex-1-onnarrow">
                  <label for="bookings-filter__searchterm" class="filter-label" style="margin-bottom: 0.25rem;">{{ totalItems }} {{ totalItems === 1 ? 'booking' : 'bookings'}}</label>
                  <CInput id="bookings-filter__searchterm" v-model="searchTerm" style="position: relative; min-width: 35ch; margin-bottom: 0.25rem;" placeholder="Booking no., customer or guest name" @keydown.enter="onSearch">
                    <template #append>
                      <div style="position: absolute; top: 0; right: 0; margin-right: .5rem; z-index: 10; cursor: pointer; display: flex; align-items: center; height: calc(1.5em + 0.75rem + 2px);" @click="onClearSearch()">
                        <CIcon v-show="searchTerm?.trim().length > 0" name="cilX" />
                      </div>
                    </template>
                  </CInput>
                </div>
                <div style="display: flex; gap: .5rem 1rem;">
                  <div style="margin-bottom: .25rem;">
                    <label for="bookings-filter__createddates" class="filter-label" style="margin-bottom: 0;">Created</label>
                    <div id="bookings-filter__createddates" style="display: flex; gap: 1rem;">
                      <DatePicker :value.sync="activeBookingsFilter.createdFrom" placeholder="From" />
                      <DatePicker :value.sync="activeBookingsFilter.createdTo" placeholder="To" />
                    </div>
                  </div>
                  <div>
                    <label for="bookings-filter__traveldates" class="filter-label" style="margin: 0;">Travel Dates</label>
                    <div id="bookings-filter__traveldates" style="display: flex; gap: 1rem;">
                      <DatePicker :value.sync="activeBookingsFilter.startDate" placeholder="From" />
                      <DatePicker :value.sync="activeBookingsFilter.endDate" placeholder="To" />
                    </div>
                  </div>
                </div>
                <div>
                  <label for="bookings-filter__experience" class="filter-label" style="margin-bottom: 0.25rem;">Experience</label>
                  <CSelect id="bookings-filter__experience" :value.sync="activeBookingsFilter.experienceId" style="margin-bottom: 0.25rem;" :plain="true" :options="experiences" placeholder="Select an experience" />
                </div>
                <div style="margin-bottom: .25rem;">
                  <label for="bookings-filter__status" class="filter-label" style="margin-bottom: 0.25rem;">Status</label>
                  <CMultiSelect
                    id="bookings-filter__status"
                    :value="activeBookingsFilter.paymentStatusValues"
                    :key="paymentStatusesKey"
                    :multiple="true"
                    :selection="true"
                    style="min-width: 20ch; margin-bottom: 0;"
                    :options="paymentStatusOptions"
                    :selected="activeBookingsFilter.paymentStatusValues"
                    optionsEmptyPlaceholder="No options placeholder"
                    selectionType="text"
                    @update="appendValue($event)"
                  />
                </div>
                <div class="bookings-filter__linebreak"></div>
                <div style="display: flex; gap: .5rem; margin-bottom: .25rem;">
                  <CButton color="primary" @click="onSearch" class="btn" style="height: fit-content;">Search</CButton>
                  <CButton color="secondary" @click="onResetFilter" class="btn" style="height: fit-content;">Clear</CButton>
                </div>
              </CCol>
            </CRow>
            <CDataTable
              hover
              :sorter="{ external: true }"
              :items="items"
              :itemsPerPageSelect="{ values: [10, 20, 50, 100] }"
              :fields="fields"
              :items-per-page="pageSize"
              @pagination-change="onPaginationChange"
              @update:sorter-value="onSorterChange"
            >
              <template #bookingNumber="{ item }">
                <td>
                  <strong>{{ item.bookingNumber }}</strong>
                </td>
              </template>
              <template #customer="{ item }">
                <td>
                  <strong>{{ item.fullName }} </strong>
                </td>
              </template>
              <template #bookingDates="{ item }">
                <td>
                  <strong>{{ moment(new Date(item.bookingDates.split(" - ")[0])).format($dateFormat).split(" ")[0] + " - "
                    + moment(new Date(item.bookingDates.split(" - ")[1])).format($dateFormat).split(" ")[0] }} </strong>
                </td>
              </template>
              <template #guestNames="{ item }">
                <td>
                  <strong>{{ item.guestNames }} </strong>
                </td>
              </template>
              <template #createDate="{ item }">
                <td>
                  <strong>{{ moment(new Date(item.createDate)).format($dateFormat) }}</strong>
                </td>
              </template>
              <template #bookingTotal="{ item }">
                <td>
                  <strong>{{ item.bookingTotal }}</strong>
                </td>
              </template>
              <template #state="{ item }">
                <td>
                  <CBadge v-if="item.state < 3" :color="getBadge(item.state)">
                    {{ item.state == 2 ? "Paid" : "Unpaid" }}
                  </CBadge>
                  <CBadge v-if="item.state == 3" color="danger">
                    Cancelled
                  </CBadge>
                  <CBadge v-if="item.state == 4" color="danger">
                    Expired
                  </CBadge>
                  <CBadge v-if="item.state == 5" color="light">
                    Confirmed
                  </CBadge>
                  <CBadge v-if="item.state == 6" color="success">
                    Partial Paid
                  </CBadge>
                </td>
              </template>
              <template #ACTIONS="{ item }">
                <td class="action-buttons">
                  <CButton color="info" @click="getDetails(item.id)">
                    <CIcon name="cil-list" /> Details
                  </CButton>
                  <CButton v-if="user?.hasGlobalRights" color="danger" @click="onClickDeleteBooking(item.id)">
                    <CIcon name="cil-trash" /> Delete
                  </CButton>
                </td>
              </template>
            </CDataTable>
            <CPagination
              v-if="true"
              v-show="true"
              :activePage="activeBookingsFilter.pageNumber"
              :limit="10"
              :pages="totalPages"
              @update:activePage="onActivePageChange"
            />
            <CModal v-model="deleteBooking" title="Are you sure to delete selected booking ?" color="danger"
              :show.sync="warningModal" @update:show="closeModalDeleteBooking">
              <CRow>
                <CCol col="9">
                  <p>You are about to delete the selected booking with id {{ deleteBooking }}!</p>
                </CCol>
              </CRow>
            </CModal>
          </CCardBody>
        </CCard>
      </transition>
    </CCol>
    <CSpinner style="display: block; position: fixed; top: 50%; left: 50%" v-if="isResetRequested === true" color="primary" />
  </CRow>
</template>
<script>
import axios from "axios";
import { defaultPaymentStatuses } from '@/common';
import DatePicker from "@/components/DatePicker.vue"

export default {
  props: {
    bookingsFilter: { type: Object, default: () => ({}) },
    items: [],
    totalItems: { type: Number, default: 0 },
    pageSize: { type: Number, default: 20 },
  },
  emits: ['update:filter', 'reset'],
  name: "BookingsTable",
  components: {
    DatePicker,
  },
  data: function () {
    return {
      // table filter
      searchTerm: null,
      defaultFilter: {
        search: null,
        createdFrom: null,
        createdTo: null,
        startDate: null,
        endDate: null,
        experienceId: null,
        paymentStatusValues: defaultPaymentStatuses,
        sort: null
      },
      activeBookingsFilter: {},
      paymentStatusesKey: new Date().valueOf(),
      // other
      paymentStatusOptions: [
        {
          value: 1,
          text: "Unpaid",
        },
        {
          value: 2,
          text: "Paid",
        },
        {
          value: 3,
          text: "Canceled",
        },
        {
          value: 4,
          text: "Expired",
        },
        {
          value: 5,
          text: "Confirmed",
        },
        {
          value: 6,
          text: "PartialPaid",
        },
      ],
      warningModal: false,
      deleteBooking: null,
      experiences: [],
      isResetRequested: false,
      user: null,
      fields: [
        { key: "bookingNumber", sorter: true },
        { key: "bookingDates", sorter: false },
        { key: "customer", sorter: true },
        { key:"guestNames", sorter: false },
        { key: "createDate", sorter: true },
        { key: "bookingTotal", sorter: false },
        { key: "state", sorter: false },
        { key: "ACTIONS", sorter: false },
      ],
      //Alert
      alertType: "danger",
      message: null,
     
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize) || 1
    },
  },
  methods: {
    appendValue(event) {
      this.activeBookingsFilter.paymentStatusValues = event;
    },
    getBadge(status) {
      switch (status) {
        case 2:
          return "success";
        case 1:
          return "secondary";
        default:
          return "primary";
      }
    },
    formatSimpleDate(dateValue) {
      const date = new Date(dateValue);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    searchBookings({ pageSize = this.pageSize } = {}) {
      const formattedFilter = {
        pageNumber: this.activeBookingsFilter.pageNumber,
        pageSize,
        search: this.searchTerm || undefined,
        createdFrom: this.activeBookingsFilter.createdFrom ? this.formatSimpleDate(this.activeBookingsFilter.createdFrom) : undefined,
        createdTo: this.activeBookingsFilter.createdTo ? this.formatSimpleDate(this.activeBookingsFilter.createdTo) : undefined,
        startDate: this.activeBookingsFilter.startDate ? this.formatSimpleDate(this.activeBookingsFilter.startDate) : undefined,
        endDate: this.activeBookingsFilter.endDate ? this.formatSimpleDate(this.activeBookingsFilter.endDate) : undefined,
        experienceId: this.activeBookingsFilter.experienceId ?? undefined,
        paymentStatusValues: this.activeBookingsFilter.paymentStatusValues,
        sort: this.activeBookingsFilter.sort,
      };
      this.$emit('update:filter', formattedFilter);
    },
    onSearch() {
      this.activeBookingsFilter.pageNumber = 1;
      this.searchBookings();
    },
    onActivePageChange(page) {
      this.activeBookingsFilter.pageNumber = page;
      this.searchBookings();
    },
    onPaginationChange(newPageSize) {
      this.searchBookings({ pageSize: newPageSize})
    },
    /**
     * @param {Object} sorterState
     * @param {boolean} sorterState.asc
     * @param {string} sorterState.column
     */
    onSorterChange(sorterState) {
      this.activeBookingsFilter.sort = `${sorterState.column} ${sorterState.asc ? 'asc' : 'desc'}`;
      this.searchBookings();
    },
    onClearSearch() {
      this.searchTerm = null;
      this.activeBookingsFilter.pageNumber = 1;
      this.searchBookings();
    },
    onResetFilter() {
      this.$emit('reset', { pageSize: this.pageSize, sort: this.activeBookingsFilter.sort });
      this.paymentStatusesKey = new Date().valueOf();
    },
    onClickDeleteBooking(bookingId) {
      this.deleteBooking = bookingId;
      this.warningModal = true;
    },
    closeModalDeleteBooking(status, evt, accept) {
      let self = this;
      if (accept) {
        self.isResetRequested = true;
        axios
          .delete(`${this.$apiAdress}/v1/Booking/${self.deleteBooking}`)
          .then(function (response) {
            self.deleteBooking = null;
            self.warningModal = false;
            self.isResetRequested = false;
            self.searchBookings();
          })
          .catch(function (error) {
            self.deleteBooking = null;
            self.warningModal = false;
            self.isResetRequested = false;
          });
      } else {
        this.deleteBooking = null;
        this.warningModal = false;
        self.isResetRequested = false;
      }
    },
    getDetails(id) {
      if (id != "00000000-0000-0000-0000-000000000000" && id != null) {
        const link = `/bookings/details/${id.toString()}`;
        this.$router.push({ path: link });
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    getExperienceList() {
      let self = this;
      axios
        .get(this.$apiAdress + "/v1/ListItem/ExperienceList")
        .then((response) => {
          self.experiences = response.data;
        })
        .catch(function (error) {
          self.alertType = "danger";
          setTimeout(function () {
            self.message = null;
          }, 10000);
          self.message = error;
        });
    },
  },
  created() {
    this.activeBookingsFilter = { ...this.defaultFilter, ...this.bookingFilter };
  },
  mounted: function () {
    var user = localStorage.getItem("user");
    if (user != null) {
      this.user = JSON.parse(localStorage.getItem("user"));
    }
    this.getExperienceList("ExperienceList");
  },
  watch: {
    bookingsFilter(newVal) {
      if (!newVal) return;
      const { pageNumber, search, createdFrom, createdTo, startDate, endDate, experienceId, paymentStatusValues, sort } = newVal;
      const updatedFilter = {
        pageNumber: pageNumber ?? 1,
        createdFrom: createdFrom ? new Date(createdFrom) : null,
        createdTo: createdTo ? new Date(createdTo) : null,
        startDate: startDate ? new Date(startDate) : null,
        endDate: endDate ? new Date(endDate) : null,
        experienceId: experienceId ?? null,
        paymentStatusValues: paymentStatusValues ?? [],
        sort: sort ?? null,
      }
      this.activeBookingsFilter = updatedFilter;
      this.searchTerm = search ?? null;
    },
  }
};
</script>
<style>
::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  -webkit-border-radius: 8px;
}

::-webkit-scrollbar-thumb {
  -webkit-border-radius: 10px;
  border-radius: 10px;
  background: rgba(100, 100, 100, 0.8);
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
}
</style>
<style scoped>
.flex-1-onnarrow {
  flex: 0;
  @media screen and (max-width: 1440px) {
    flex: 1;
  }
}
.filter-label {
  font-weight: 600;
}
.bookings-filter__linebreak {
  width: 100%;
  @media screen and (min-width: 1900px) {
    width: 0;
  }
}
</style>